import React, {useState, useEffect, Fragment, Suspense} from 'react';
import parse from 'html-react-parser';
import isNil from 'lodash/isNil';
import replace from 'lodash/replace';
import {isJSONstring} from '../../../utils/storage';
import W from '../../common/Word';
import { useLocation } from 'react-router-dom';
import {currentLang, dynamic_delivery_promise} from '../../../utils/functions';
import {getStorage} from '../../../utils/storage';
import { useTranslation } from 'react-i18next';

// Which modules appear on a page is decided by the CMS, and any given page uses
// only a handful — but importing all of them statically put every module (and
// their deps, notably Swiper via Slider) into the initial bundle on every route.
// Loading them on demand means a page downloads only the modules it renders.
// The `text_box` / `html_box` types are intentionally NOT here: they render
// inline via `parse()` above, are by far the most common, and include the
// CMS-authored hero markup — so they must stay in the initial bundle.
const Slider = React.lazy(() => import('../Slider'));
const Freezer = React.lazy(() => import('../Freezer'));
const FarmerBanner = React.lazy(() => import('../FarmerBanner'));
const Category = React.lazy(() => import('../Category'));
const RelatedProducts = React.lazy(() => import('../RelatedProducts'));
const RelatedRecipes = React.lazy(() => import('../RelatedRecipes'));
const MeatcutOverview = React.lazy(() => import('../MeatcutOverview'));
const RecipeHighlight = React.lazy(() => import('../RecipeHighlight'));
const ProductHighlight = React.lazy(() => import('../ProductHighlight'));
const Newsletter = React.lazy(() => import('../Newsletter'));
const NewsletterPopup = React.lazy(() => import('../NewsletterPopup'));
const BreedsOverview = React.lazy(() => import('../BreedsOverview'));
const Reviews = React.lazy(() => import('../Reviews'));
const Navigator = React.lazy(() => import('../Navigator'));
const BasicRecipe = React.lazy(() => import('../BasicRecipe'));
const FarmshopHighlight = React.lazy(() => import('../FarmshopHighlight'));
const BlogHighlight = React.lazy(() => import('../BlogHighlight'));
const FarmshopPopularProducts = React.lazy(() => import('../FarmshopPopularProducts'));

function LoadModule({data, product, filter, moduleData, moduleId}) {
    const [moduleType, setModuleType] = useState();
    const [moduleConfig, setModuleConfig] = useState(null);
    const [items, setItems] = useState();
    const [moduleClass, setModuleClass] = useState(null);
    const { t } = useTranslation();

    // temp
    let location = useLocation();
    let urlpaths = replace(location.pathname, '/'+currentLang()+'/', '').split('/');
    let slug = urlpaths[0];
    useEffect(() => {
        if(!isNil(data.config)){
            if(isJSONstring(data.config)){
                setModuleConfig(JSON.parse(data.config));
            }else{
                setModuleConfig(data.config);
            }
        }
        setModuleType(data.module);
        if(data && moduleType == 'html_box'){
            var webp_disable = getStorage('webp_disable');
            if(webp_disable){
                data.body = data.body.replaceAll('.webp', '.jpg').replaceAll('image/webp', 'image/jpeg');
            }
        }

        // Dynamic delivery promise
        if(data){
            let dynamic_delivery_promise_text = t(dynamic_delivery_promise(t('farmshop.deadline_hour')));

            if(data.module == 'slider'){
                if(data.slider_items && data.slider_items.length > 0){
                    data.slider_items.map((item, index)=>{
                        if(data.slider_items[index].body){
                            data.slider_items[index].body = data.slider_items[index].body.replaceAll('{dynamic_delivery_promise}', dynamic_delivery_promise_text );
                        }
                    })
                }
            }else if(['html_box','popular_farmshop','farmshop_highlight'].includes(data.module)){
                if(data.body){
                    data.body = data.body.replaceAll('{dynamic_delivery_promise}', dynamic_delivery_promise_text );
                }
            }
            var className = data.class ? data.class : ' ';
            setModuleClass(className);
        }

    }, [data])


    return (
        <div>
            {/* fallback={null} on purpose: modules are independent content blocks,
                so a placeholder would only add a visible jump when the chunk lands. */}
            <Suspense fallback={null}>
            {data && moduleType == 'slider' &&
                <Slider data={data.slider_items} config={moduleConfig} />
            }
            {data && moduleType == 'text_box' &&
                <div className={"container mt-4 module__text-box"}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <div className="row">
                    <div className="col-12">
                        <h2>{data.title}</h2>
                        {data.body && parse(data.body)}
                    </div>
                    </div>
                </div>
            }
            {data && moduleType == 'html_box' &&
                <div className={"module__html-box "+moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    {slug == 'invest' &&
                        <div dangerouslySetInnerHTML={{__html: data.body}}></div>
                    }
                    {slug != 'invest' &&
                        <Fragment>
                        {data.body && parse(data.body)}
                        </Fragment>
                    }
                </div>
            }
            {data && moduleType == 'freezer_size' &&
                <div className={"module__freezer-size"}>
                    <Freezer config={moduleConfig} weight={product && product.shop_package_frontend_size} />
                </div>
            }
            {data && moduleType === 'animal_switch' &&
                <div className={moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <Category filter={filter} config={moduleConfig} />
                </div>
            }
            {data && moduleType === 'farmer_banner' &&
                <FarmerBanner data={moduleData} />
            }
            {moduleData && moduleType === 'related_products' &&
                <div className={moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <RelatedProducts data={data} relatedType={moduleData.relatedType} relatedSlug={moduleData.relatedSlug} />
                </div>
            }
            {moduleData && moduleType === 'related_recipes' &&
                <div className={moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <RelatedRecipes data={data} relatedType={moduleData.relatedType} relatedSlug={moduleData.relatedSlug} />
                </div>
            }
            {data && moduleType == 'related_meatcuts' &&
                <div className={"module__meatcut "+moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <MeatcutOverview data={data} />
                </div>
            }
            {data && moduleType == 'related_breeds' &&
                <div className={"module__breeds "+moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <BreedsOverview data={data} items={data.items} />
                </div>
            }
            {data && moduleType == 'related_reviews' &&
                <div className={"module__reviews "+moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <Reviews data={data} items={data.items} />
                </div>
            }
            {data && moduleType == 'recipe_highlight' &&
                <div className={moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <RecipeHighlight data={data} items={data.items} />
                </div>
            }
            {data && moduleType == 'product_card' &&
                <div>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <ProductHighlight data={data} items={data.items} moduleClass={moduleClass} />
                </div>
            }
            {data && moduleType == 'newsletter' && moduleConfig.position != 'popup' &&
                <Newsletter data={moduleConfig} />
            }
            {data && moduleType == 'newsletter' && moduleConfig.position == 'popup' &&
                <NewsletterPopup data={moduleConfig} />
            }
            {data && moduleType == 'navigator' &&
                <div className={"module__navigator "+moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <Navigator data={data} />
                </div>
            }
            {data && moduleType == 'basic_recipe' &&
                <div className={moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <BasicRecipe data={data.recipe && data.recipe} />
                </div>
            }
            {data && moduleType == 'farmshop_highlight' &&
                <div className={"module__farmshop-highlight "+moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <FarmshopHighlight data={data} />
                </div>
            }
            {data && moduleType == 'blog_highlight' &&
                <div className={"module__blog_highlight "+moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    <BlogHighlight data={data} />
                </div>
            }
            {data && moduleType == 'popular_farmshop' &&
                <div className={"module__popular_farmshop "+moduleClass}>
                    {data.anchor && <a name={data.anchor+'-'+moduleId}></a>}
                    {<FarmshopPopularProducts data={data} />}
                </div>

            }
            </Suspense>
        </div>
    )

}

export default LoadModule
